<!--
  Collapsible palette section: header with badge + list of PassportBuilderFieldChip.
-->
<script setup lang="ts">
import { computed } from 'vue'
import type { DragPayload, PaletteField, PaletteGroup, PaletteGroupKind } from '../model/types'
import { createDragPayload } from '../model/useDragDrop'
import { VIcons } from '@/shared/ui'
import PassportBuilderFieldChip from './PassportBuilderFieldChip.vue'

const props = withDefaults(
  defineProps<{
    group: PaletteGroup
    /** Indented under «Справочники» (parent list_types / родитель groups). */
    nested?: boolean
    /** Top-level «Собственные поля»: show edit control in header. */
    showEdit?: boolean
  }>(),
  { nested: false, showEdit: false },
)

const emit = defineEmits<{
  toggle: [groupId: string]
  dragstart: [payload: DragPayload, event: DragEvent]
  dragend: []
  edit: []
  'field-activate': [field: PaletteField]
}>()

const badgeClassByKind: Record<PaletteGroupKind, string> = {
  own: 'pb-badge-own',
  parent: 'pb-badge-parent',
  list: 'pb-badge-list',
  element: 'pb-badge-element',
}

const badgeClass = computed(() => badgeClassByKind[props.group.kind])

const depthWarningClass = computed(() => ({
  'pb-sidebar-group-depth-warning--parent': props.group.kind === 'parent',
  'pb-sidebar-group-depth-warning--list': props.group.kind === 'list',
  'pb-sidebar-group-depth-warning--element': props.group.kind === 'element',
}))

/** LIST перечень: table is dragged from the title row, not from a separate chip. */
const isListTableDrag = computed(
  () => props.group.kind === 'list' && props.group.listTableDrag != null,
)

function onHeaderClick() {
  emit('toggle', props.group.id)
}

function onListTableDragStart(event: DragEvent) {
  const d = props.group.listTableDrag
  if (!d) return
  const payload = createDragPayload('palette', 'table', d)
  emit('dragstart', payload, event)
}
</script>

<template>
  <div class="pb-sidebar-group" :class="{ 'pb-sidebar-group--nested': nested }">
    <div
      class="pb-sidebar-group-header"
      :class="{
        'pb-sidebar-group-header--open': group.open,
        'pb-sidebar-group-header--list-drag': isListTableDrag,
      }"
      role="button"
      tabindex="0"
      @click="!isListTableDrag ? onHeaderClick() : undefined"
      @keydown.enter.prevent="onHeaderClick"
      @keydown.space.prevent="onHeaderClick"
    >
      <span
        class="pb-sidebar-group-arrow"
        aria-hidden="true"
        @click.stop="isListTableDrag ? onHeaderClick() : undefined"
      >▶</span>
      <VBtn
        v-if="showEdit && group.kind !== 'list'"
        type="button"
        icon
        variant="text"
        density="compact"
        size="x-small"
        class="pb-sidebar-group-edit pb-sidebar-group-edit--sm"
        :class="`pb-sidebar-group-edit--${group.kind}`"
        :aria-label="$t('itf.edit')"
        @click.stop="emit('edit')"
      >
        <VIcons type="settings" />
      </VBtn>
      <div
        v-if="isListTableDrag"
        class="pb-sidebar-group-header__list-drag"
        draggable="true"
        :aria-label="
          `${$t('itf.passport_builder_list_drag_table_hint')}: ${group.title}`.trim()
        "
        @dragstart="onListTableDragStart"
        @click.stop
      >
        <span class="pb-sidebar-group-header__list-drag-label">
          <span
            class="pb-sidebar-group-header__list-drag-icon-wrap"
            :title="$t('itf.passport_builder_list_drag_table_hint')"
          >
            <VIcons
              type="table_chart"
              class="pb-sidebar-group-header__list-table-icon"
              aria-hidden="true"
            />
          </span>
          <span class="pb-sidebar-group-header__title-stack">
            <span class="pb-sidebar-group-header__title" :title="group.title">{{ group.title }}</span>
            <span
              v-if="group.subtitle && group.subtitle.trim() !== group.title.trim()"
              class="pb-sidebar-group-header__subtitle"
            >{{ group.subtitle }}</span>
          </span>
        </span>
        <span class="pb-sidebar-group-header__meta">
          <v-tooltip v-if="group.nestedDepthLimitReached" location="top">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="pb-sidebar-group-depth-warning"
                :class="depthWarningClass"
                role="img"
                :aria-label="$t('itf.passport_builder_parent_types_depth_limit')"
              >
                <VIcons type="warning" />
              </span>
            </template>
            <span>{{ $t('itf.passport_builder_parent_types_depth_limit') }}</span>
          </v-tooltip>
          <span class="pb-sidebar-group-badge" :class="badgeClass">{{ group.fields.length }}</span>
        </span>
      </div>
      <template v-else>
        <span class="pb-sidebar-group-header__title-stack">
          <span class="pb-sidebar-group-header__title" :title="group.title">{{ group.title }}</span>
          <span v-if="group.subtitle" class="pb-sidebar-group-header__subtitle">{{ group.subtitle }}</span>
        </span>
        <span class="pb-sidebar-group-header__meta">
          <v-tooltip v-if="group.nestedDepthLimitReached" location="top">
            <template #activator="{ props: tooltipProps }">
              <span
                v-bind="tooltipProps"
                class="pb-sidebar-group-depth-warning"
                :class="depthWarningClass"
                role="img"
                :aria-label="$t('itf.passport_builder_parent_types_depth_limit')"
              >
                <VIcons type="warning" />
              </span>
            </template>
            <span>{{ $t('itf.passport_builder_parent_types_depth_limit') }}</span>
          </v-tooltip>
          <span class="pb-sidebar-group-badge" :class="badgeClass">{{ group.fields.length }}</span>
        </span>
      </template>
    </div>
    <div v-show="group.open" class="pb-sidebar-group-items">
      <PassportBuilderFieldChip
        v-for="f in group.fields"
        :key="f.id"
        :field="f"
        :group-kind="group.kind"
        @dragstart="(p, e) => emit('dragstart', p, e)"
        @activate="emit('field-activate', $event)"
      />
      <PassportBuilderSidebarGroup
        v-for="child in group.children"
        :key="child.id"
        nested
        :group="child"
        @toggle="emit('toggle', $event)"
        @dragstart="(p, e) => emit('dragstart', p, e)"
        @field-activate="emit('field-activate', $event)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
/* LIST перечень: do not stretch header cross-axis — stretch made ▶ sit at top of a tall row. */
.pb-sidebar-group-header--list-drag {
  align-items: center;
  cursor: default;
}

.pb-sidebar-group-header--list-drag .pb-sidebar-group-arrow {
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.pb-sidebar-group-header__list-drag {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 6px;
  cursor: grab;
  padding: 5px 7px;
  margin: -2px -4px;
  border-radius: 6px;
  user-select: none;
  border: 1px dashed var(--pb-amber-border, rgba(245, 124, 0, 0.45));
  background: var(--pb-amber-bg, rgba(245, 124, 0, 0.1));
  box-shadow: 0 0 0 1px rgba(245, 124, 0, 0.06);
  transition:
    background 0.12s,
    border-color 0.12s,
    box-shadow 0.12s;

  &:hover {
    background: rgba(245, 124, 0, 0.16);
    border-color: var(--pb-amber, #f57c00);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .pb-sidebar-group-header__meta {
    margin-left: auto;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    align-self: center;
  }

  &:active {
    cursor: grabbing;
  }
}

/* Icon + title: vertical center (baseline made label vs badge misaligned). */
.pb-sidebar-group-header__list-drag-label {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;
  min-width: 0;
}

.pb-sidebar-group-header__title-stack {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 1px;
  line-height: 1.15;
}

.pb-sidebar-group-header__subtitle {
  font-size: 11px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.58);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pb-sidebar-group-header__list-drag-icon-wrap {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.pb-sidebar-group-header__list-drag .pb-sidebar-group-header__title-stack {
  flex: 1;
  min-width: 0;
}

.pb-sidebar-group-header__list-drag .pb-sidebar-group-header__title {
  line-height: 1.15;
}

.pb-sidebar-group-header__list-table-icon {
  flex-shrink: 0;
  font-size: 13px !important;
  width: 15px;
  height: 15px;
  line-height: 1;
  color: var(--pb-amber, #f57c00);
  /* Material glyph sits high in em-box; nudge down to match cap height of label */
  transform: translateY(1px);
}

.pb-sidebar-group-depth-warning {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  cursor: help;
  line-height: 1;
}

.pb-sidebar-group-depth-warning :deep(.v-icon) {
  font-size: 16px !important;
  width: 16px;
  height: 16px;
}

.pb-sidebar-group-depth-warning--parent :deep(.v-icon) {
  color: var(--pb-teal, #00897b);
}

.pb-sidebar-group-depth-warning--list :deep(.v-icon) {
  color: var(--pb-amber, #f57c00);
}
</style>
